import { useState } from "react";
import { toast } from "sonner";
import { GitFork } from "lucide-react";
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  Button,
} from "@/design/components";
import { useSeed, useActorId, usePeopleIndex, useMoney, useMoneyParts } from "@/domain/hooks";
import { useDisbursementActions } from "@/domain/useMutations";
import { MULTISIG_THRESHOLD, type Disbursement } from "@/domain/types";
import { VerificationSummaryCard } from "@/screens/approval/VerificationSummaryCard";
import { useTrace, EvidenceSection, TraceRow } from "@/shell/TraceProvider";
import { useTraceNode } from "@/shell/trace/useTraceNode";

export function FunderApprovalDrawer({
  disbursement,
  open,
  onOpenChange,
}: {
  disbursement: Disbursement | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const seed = useSeed();
  const actorId = useActorId();
  const people = usePeopleIndex();
  const money = useMoney();
  const moneyParts = useMoneyParts();
  const { approve: approveDisb, query: queryDisb, hold: holdDisb, isPending } = useDisbursementActions();
  const [mode, setMode] = useState<null | "query" | "hold">(null);
  const [comment, setComment] = useState("");
  const { openTrace } = useTrace();
  // Same resolver + rendering the canonical Trace Source panel uses (see
  // TraceProvider.tsx) - real mode and demo mode both work correctly here,
  // unlike the old hand-rolled seed.spendRequests/seed.evidence derivation
  // this replaced, which only ever worked in demo mode.
  const { data: trace } = useTraceNode(disbursement ? { kind: "disbursement", id: disbursement.id } : undefined);

  if (!disbursement) return null;
  const d = disbursement;
  const milestone = seed.milestones.find((m) => m.id === d.milestoneId);
  const project = seed.projects.find((p) => p.id === milestone?.projectId);

  // Multi-signature (v3 §Chunk 7): large tranches need two distinct funder sign-offs.
  const needsTwo = d.amount >= MULTISIG_THRESHOLD;
  const approvals = d.approvals ?? [];
  const alreadySigned = approvals.some((a) => a.approverId === actorId);
  const willComplete = !needsTwo || approvals.length >= 1;
  const amt = moneyParts(d.amount);
  // Approve restates the amount being released, so the irreversible action is explicit.
  const approveLabel = needsTwo
    ? approvals.length === 0
      ? `Sign off (1 of 2) · ${amt.value} ${amt.unit}`
      : `Approve ${amt.value} ${amt.unit} · final sign-off`
    : `Approve ${amt.value} ${amt.unit}`;

  async function approve() {
    await approveDisb(d.id);
    toast.success(willComplete ? `Disbursement approved · ${milestone?.name}.` : "Signed off. A second authoriser is required to release.");
    onOpenChange(false);
  }
  async function submitMode() {
    if (!comment.trim()) return;
    if (mode === "hold") {
      await holdDisb(d.id, comment);
      toast.message("Disbursement rejected · returned to Stratus");
    } else {
      await queryDisb(d.id, comment);
      toast.message("Query sent to Stratus");
    }
    setMode(null);
    setComment("");
    onOpenChange(false);
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange} width="680px">
      <DrawerHeader>
        <DrawerTitle className="text-base font-semibold text-ink">{milestone?.name}</DrawerTitle>
        <p className="mt-1 text-xs text-faint">{project?.name} · {seed.programme.name}</p>
        <p className="mt-1 text-lg font-semibold tabular-nums text-ink">{money(d.amount)}</p>
      </DrawerHeader>

      <DrawerBody className="space-y-6">
        {milestone && (
          <VerificationSummaryCard
            milestoneId={milestone.id}
            reviewerName={d.routedById ? people[d.routedById]?.fullName : undefined}
          />
        )}

        <section>
          <div className="mb-2 flex items-center justify-between gap-2">
            <h4 className="text-meta text-faint">Justification · confirmed spend</h4>
            <button
              type="button"
              onClick={() => openTrace({ kind: "disbursement", id: d.id }, { from: "Funder approval review" })}
              className="flex shrink-0 items-center gap-1 text-xs font-medium text-brand hover:underline"
            >
              <GitFork className="h-3.5 w-3.5" /> View full trace
            </button>
          </div>
          <p className="mb-2 text-xs text-mute">
            This tranche is justified by the confirmed, evidenced field spend on the milestone. Approving releases the funds and unlocks the next milestone.
          </p>
          {!trace?.down.length ? (
            <p className="text-sm text-faint">The basis is summarised in the Stratus recommendation below.</p>
          ) : (
            <div className="space-y-1.5">
              {trace.down.map((node) => (
                <TraceRow
                  key={node.target ? `${node.target.kind}-${node.target.id}` : node.label}
                  node={node}
                  money={money}
                  onNavigate={(t) => openTrace(t, { from: "Funder approval review" })}
                />
              ))}
            </div>
          )}
        </section>

        <section>
          <h4 className="text-meta mb-2 text-faint">Stratus recommendation</h4>
          <p className="rounded-md bg-subtle px-3 py-3 text-sm leading-relaxed text-mute">{d.stratusRecommendation}</p>
        </section>

        {trace && <EvidenceSection result={trace} />}

        <section>
          <h4 className="text-meta mb-2 text-faint">Comments</h4>
          <div className="rounded-md border border-line px-3 py-2 text-sm text-mute">
            <span className="font-medium text-ink">{people["per-sarah"]?.fullName ?? "Tom Edwin Makopiyo"}</span> · Reviewed and recommended for disbursement.
            <span className="ml-2 text-[11px] text-faint">Shared with funder</span>
          </div>
        </section>
      </DrawerBody>

      <DrawerFooter className="flex-col items-stretch gap-2">
        {needsTwo && (
          <p className="text-xs text-mute">
            Multi-signature: <span className="font-medium tabular-nums text-ink">{approvals.length} of 2</span> sign-offs.
            {approvals.length > 0 && ` Signed by ${approvals.map((a) => people[a.approverId]?.fullName ?? a.approverId).join(", ")}.`}
          </p>
        )}
        {mode ? (
          <div className="space-y-2">
            <textarea
              autoFocus
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={mode === "hold" ? "Reason for rejection (required)" : "Query for Stratus (required)"}
              className="h-20 w-full rounded-md border border-line p-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
            />
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="ghost" className="w-full sm:w-auto" onClick={() => { setMode(null); setComment(""); }}>Cancel</Button>
              <Button variant={mode === "hold" ? "danger" : "primary"} className="w-full sm:w-auto" onClick={submitMode} disabled={isPending || !comment.trim()}>{mode === "hold" ? "Reject disbursement" : "Send query"}</Button>
            </div>
          </div>
        ) : (
          // Stacks to one full-width button per row below sm: every Button
          // is whitespace-nowrap by design (Button.tsx), and approveLabel
          // can run long ("Sign off (1 of 2) · 1,234,567 GBP") - the old
          // single-row flex had no wrap and no scroll escape, so on a phone
          // the approve button (the one that actually releases funds) was
          // the one most likely to get squeezed or pushed out of reach.
          // flex-col-reverse puts Approve - the primary action - first/top
          // on mobile while keeping the desktop left-to-right order
          // (Reject, Query, Approve) via sm:flex-row.
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Button variant="ghost" className="w-full text-bad hover:bg-badSoft hover:text-bad sm:w-auto" disabled={isPending} onClick={() => setMode("hold")}>Reject</Button>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
              <Button variant="secondary" className="w-full sm:w-auto" disabled={isPending} onClick={() => setMode("query")}>Query</Button>
              <Button className="w-full sm:w-auto" onClick={approve} disabled={isPending || alreadySigned} title={alreadySigned ? "You have already signed; a second authoriser is required" : undefined}>
                {alreadySigned ? "Awaiting second authoriser" : approveLabel}
              </Button>
            </div>
          </div>
        )}
      </DrawerFooter>
    </Drawer>
  );
}
